import {readMedicationDose} from '../../shared/medicationDose';
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import LogFormShell from '../../components/UI/LogFormShell';
import colors from '../../assets/theme/colors';

const HealthCandidateReviewSheet = ({ open, candidate, saving, onClose, onSave }) => {
  const [label, setLabel] = useState('');
  const [dose, setDose] = useState('');
  const [schedule, setSchedule] = useState('');
  const [holdStartDate, setHoldStartDate] = useState('');
  const [resumeDate, setResumeDate] = useState('');
  const [reason, setReason] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [provider, setProvider] = useState('');
  const [historicalDate, setHistoricalDate] = useState('');
  const [historicalMedications, setHistoricalMedications] = useState([]);
  const [clarificationSelected, setClarificationSelected] = useState(false);

  useEffect(() => {
    setLabel(String(candidate?.suggestedAction === 'needs_clarification'
      ? candidate?.extracted?.name || ''
      : candidate?.label || '').trim());
    setDose([candidate?.extracted?.doseText || candidate?.extracted?.dose, candidate?.extracted?.doseText ? '' : candidate?.extracted?.unit].filter(Boolean).join(' ').trim());
    setSchedule(String(candidate?.extracted?.scheduleText || '').trim());
    setHoldStartDate(String(candidate?.extracted?.temporaryHold?.startDate || '').trim());
    setResumeDate(String(candidate?.extracted?.temporaryHold?.resumeDate || '').trim());
    setReason(String(candidate?.extracted?.temporaryHold?.reason || candidate?.extracted?.completedCourse?.reason || '').trim());
    setEffectiveDate(String(candidate?.extracted?.discontinuation?.effectiveDate || candidate?.extracted?.completedCourse?.effectiveDate || '').trim());
    setProvider(String(candidate?.extracted?.discontinuation?.provider || '').trim());
    setHistoricalDate(String(candidate?.extracted?.historicalSnapshot?.date || '').trim());
    setHistoricalMedications(
      Array.isArray(candidate?.extracted?.historicalSnapshot?.medications)
        ? candidate.extracted.historicalSnapshot.medications.map((medication) => ({ ...medication }))
        : []
    );
    setClarificationSelected(false);
  }, [candidate]);

  const typeLabel = candidate?.domain === 'medication' ? 'medication' : 'allergy';
  const sourceText = candidate?.sourceText || candidate?.extracted?.sourceText || '';
  const isTemporaryHold = candidate?.suggestedAction === 'temporary_hold';
  const isDiscontinuation = candidate?.suggestedAction === 'discontinue';
  const isCourseCompletion = candidate?.suggestedAction === 'complete_course';
  const isHistoricalSnapshot = candidate?.suggestedAction === 'historical_snapshot';
  const isClarification = candidate?.suggestedAction === 'needs_clarification';
  const clarification = candidate?.extracted?.canonicalMedicationRecord?.actionDetails || {};
  const conflictOptions = Array.isArray(clarification.options) ? clarification.options : [];
  const isConflictingPlan = isClarification && clarification.issue === 'conflicting_plans';
  const historicalSnapshot = candidate?.extracted?.historicalSnapshot || {};
  const medicationIsMissing = isTemporaryHold && !candidate?.matchedMedication;
  const discontinuedMedicationIsMissing = isDiscontinuation && !candidate?.matchedMedication;
  const completedMedicationIsMissing = isCourseCompletion && !candidate?.matchedMedication;
  const hasValidHoldDates = Boolean(holdStartDate && resumeDate && resumeDate > holdStartDate);
  const canSave = label.trim()
    && (!isTemporaryHold || hasValidHoldDates)
    && (!isDiscontinuation || effectiveDate)
    && (!isCourseCompletion || effectiveDate)
    && (!isHistoricalSnapshot || (
      historicalDate
      && historicalMedications.length
      && historicalMedications.every((medication) => String(medication.name || '').trim())
    ))
    && (!isClarification || (readMedicationDose({dose}).doseText && (!isConflictingPlan || clarificationSelected)));
  const updateHistoricalMedication = (index, field, value) => {
    setHistoricalMedications((current) => current.map((medication, medicationIndex) => (
      medicationIndex === index ? { ...medication, [field]: value } : medication
    )));
  };
  const parseDose = value => {const quantity=readMedicationDose({dose:value});return {dose:quantity.amount,unit:quantity.unit,doseText:quantity.doseText};};
  const getPreviousDate = (dateKey) => {
    const date = dateKey ? new Date(`${dateKey}T12:00:00Z`) : null;
    if (!date || Number.isNaN(date.getTime())) return '';
    date.setUTCDate(date.getUTCDate() - 1);
    return date.toISOString().slice(0, 10);
  };

  return (
    <LogFormShell
      open={open}
      onClose={onClose}
      title={isHistoricalSnapshot ? 'Review medication history' : isTemporaryHold ? 'Confirm temporary hold' : isDiscontinuation ? `Stop ${label || 'medication'}` : isCourseCompletion ? `Complete ${label || 'medication'} course` : isClarification ? 'Clarify medication plan' : `Review ${typeLabel}`}
      subtitle={isTemporaryHold
        ? 'Confirm the dates before medication reminders are changed.'
        : isDiscontinuation
          ? 'Confirm when this medication should stop.'
        : isHistoricalSnapshot
          ? 'Confirm when this medication list applied and where it should be saved.'
        : isCourseCompletion
          ? 'Confirm when the course ended before updating the medication list and Timeline.'
        : 'Confirm or correct this detail before it is added to the Care Profile.'}
      forceDrawer
      compactTitle
    >
      <Stack spacing={2}>
        {!isHistoricalSnapshot ? <TextField
          label={candidate?.domain === 'medication' ? 'Medication name' : 'Allergy'}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          fullWidth
          autoFocus
        /> : null}
        {isClarification ? (
          <>
            <Alert severity="warning">
              {isConflictingPlan
                ? 'These sources give different medication plans. Choose the plan that is current.'
                : 'Confirm the medication name and complete dose, including its unit, before adding it to the medication list.'}
            </Alert>
            {isConflictingPlan ? (
              <Stack spacing={1}>
                {conflictOptions.map((option, index) => {
                  const optionDose = [option.dose, option.unit].filter(Boolean).join(' ');
                  const optionLabel = [optionDose, option.schedule].filter(Boolean).join(' ');
                  return (
                    <Button
                      key={`${optionDose}-${option.schedule}-${index}`}
                      variant={clarificationSelected && dose === [option.dose, option.unit].filter(Boolean).join(' ') ? 'contained' : 'outlined'}
                      aria-label={`Use ${optionLabel}`}
                      onClick={() => {
                        setDose([option.dose, option.unit].filter(Boolean).join(' '));
                        setSchedule(String(option.schedule || ''));
                        setClarificationSelected(true);
                      }}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    >
                      <Stack alignItems="flex-start">
                        <Typography sx={{ fontWeight: 850 }}>{optionLabel}</Typography>
                        <Typography sx={{ fontSize: '0.78rem', textAlign: 'left' }}>{option.sourceText}</Typography>
                      </Stack>
                    </Button>
                  );
                })}
              </Stack>
            ) : null}
          </>
        ) : isHistoricalSnapshot ? (
          <>
            <Alert severity="info">
              This is a past medication list. It will not change current medications unless you choose that explicitly.
            </Alert>
            <TextField
              label="Timeline date"
              type="date"
              value={historicalDate}
              onChange={(event) => setHistoricalDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={historicalSnapshot.dateApproximate
                ? `Approximate date — the note only says ${historicalSnapshot.dateText}. Change it if you know the exact date.`
                : 'Enter the date this medication list applied.'}
              required
              fullWidth
            />
            <Stack spacing={1}>
              {historicalMedications.map((entry, index) => (
                <Box key={entry.id || `${entry.name}-${index}`} sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Stack spacing={1}>
                    <TextField
                      label={`Medication ${index + 1}`}
                      value={entry.name || ''}
                      onChange={(event) => updateHistoricalMedication(index, 'name', event.target.value)}
                      required
                      fullWidth
                    />
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="Dose"
                        value={[entry.dose, entry.unit].filter(Boolean).join(' ')}
                        onChange={(event) => {
                          const parsed = parseDose(event.target.value);
                          updateHistoricalMedication(index, 'dose', parsed.dose);
                          updateHistoricalMedication(index, 'unit', parsed.unit);
                        }}
                        fullWidth
                      />
                      <TextField
                        label="Schedule"
                        value={entry.schedule || ''}
                        onChange={(event) => updateHistoricalMedication(index, 'schedule', event.target.value)}
                        fullWidth
                      />
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </>
        ) : isTemporaryHold ? (
          <>
            {medicationIsMissing ? (
              <Alert severity="warning">
                {`${label || 'This medication'} is not on the medication list. The hold will be saved to Timeline without adding a current medication.`}
              </Alert>
            ) : (
              <Alert severity="info">
                Reminders will be paused during this hold and resume automatically.
              </Alert>
            )}
            <TextField label="Hold starts" type="date" value={holdStartDate} onChange={(event) => setHoldStartDate(event.target.value)} InputLabelProps={{ shrink: true }} required fullWidth />
            <TextField
              label="Resume medication"
              type="date"
              value={resumeDate}
              onChange={(event) => setResumeDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              error={Boolean(holdStartDate && resumeDate && !hasValidHoldDates)}
              helperText={holdStartDate && resumeDate && !hasValidHoldDates ? 'Resume date must be after the hold starts.' : ''}
              required
              fullWidth
            />
            <TextField label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} fullWidth />
          </>
        ) : isDiscontinuation ? (
          <>
            <Alert severity={discontinuedMedicationIsMissing ? 'warning' : 'info'}>
              {discontinuedMedicationIsMissing
                ? `${label || 'This medication'} is not on the medication list. It will not be added; the provider instruction will be marked reviewed.`
                : 'This medication will be stopped and its future reminders will be turned off.'}
            </Alert>
            <TextField
              label="Stop date"
              type="date"
              value={effectiveDate}
              onChange={(event) => setEffectiveDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />
            {provider ? <TextField label="Provider" value={provider} disabled fullWidth /> : null}
          </>
        ) : isCourseCompletion ? (
          <>
            <Alert severity="info">
              {completedMedicationIsMissing
                ? `${label || 'This medication'} is not on the active medication list. The completed course will be saved to Timeline without adding an active medication.`
                : 'This medication will move out of the active list, future reminders will stop, and the completion will be saved to Timeline.'}
            </Alert>
            <TextField
              label="Course completed"
              type="date"
              value={effectiveDate}
              onChange={(event) => setEffectiveDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={candidate?.extracted?.completedCourse?.effectiveDateText
                ? `The note says ${candidate.extracted.completedCourse.effectiveDateText}. Confirm or correct this date.`
                : 'Enter the date the final dose was completed.'}
              required
              fullWidth
            />
          </>
        ) : null}
        {sourceText ? (
          <Stack spacing={0.4}>
            <Typography sx={{ fontSize: '0.76rem', fontWeight: 850, color: colors.semantic.ink2 }}>From the original note</Typography>
            <Typography sx={{ fontSize: '0.88rem', color: colors.semantic.ink, whiteSpace: 'pre-wrap' }}>{sourceText}</Typography>
          </Stack>
        ) : null}
        {isHistoricalSnapshot ? (
          <Stack spacing={1}>
            <Button
              variant="contained"
              disabled={saving || !canSave}
              onClick={() => onSave?.({
                decisionAction: 'historical_snapshot',
                historicalSnapshot: { ...historicalSnapshot, date: historicalDate, medications: historicalMedications, dateConfirmed: true },
              })}
              sx={{ minHeight: 48, borderRadius: 999, textTransform: 'none', fontWeight: 850 }}
            >
              Save as medication history
            </Button>
            <Button
              variant="outlined"
              disabled={saving || !canSave}
              onClick={() => onSave?.({
                decisionAction: 'add_current_snapshot',
                historicalSnapshot: { ...historicalSnapshot, date: historicalDate, medications: historicalMedications, dateConfirmed: true },
              })}
              sx={{ minHeight: 48, borderRadius: 999, textTransform: 'none', fontWeight: 850 }}
            >
              These are still current
            </Button>
          </Stack>
        ) : <Button
          variant="contained"
          disabled={saving || !canSave}
          onClick={() => {
            const parsedDose = parseDose(dose);
            onSave?.(isClarification ? {
              name: label.trim(),
              dose: parsedDose.dose,
              unit: parsedDose.unit,
              sourceScheduleText: schedule.trim(),
              schedules: [{ id: 'clarified-medication-review-0', dose: parsedDose.dose, unit: parsedDose.unit, time: schedule.trim() }],
              needsReview: false,
              reviewReasons: [],
            } : isTemporaryHold ? {
              name: label.trim(),
              temporaryHold: {
                startDate: holdStartDate,
                endDate: getPreviousDate(resumeDate),
                resumeDate,
                reason: reason.trim(),
                dateConfirmed: true,
              },
            } : isDiscontinuation ? {
              name: label.trim(),
              discontinuation: {
                effectiveDate,
                provider,
                reason: candidate?.extracted?.discontinuation?.reason || 'Provider discontinued medication',
                dateConfirmed: true,
              },
            } : isCourseCompletion ? {
              name: label.trim(),
              completedCourse: {
                ...(candidate?.extracted?.completedCourse || {}),
                effectiveDate,
                reason: reason || 'Course complete',
                dateConfirmed: true,
              },
            } : label.trim());
          }}
          sx={{ minHeight: 48, borderRadius: 999, textTransform: 'none', fontWeight: 850 }}
        >
          {isClarification
            ? 'Confirm medication plan'
            : isTemporaryHold
            ? medicationIsMissing ? 'Save hold to Timeline' : 'Apply temporary hold'
            : isDiscontinuation
              ? discontinuedMedicationIsMissing ? 'Mark instruction reviewed' : 'Confirm and stop medication'
              : isCourseCompletion
                ? completedMedicationIsMissing ? 'Save completion to Timeline' : 'Complete course'
              : 'Confirm and add'}
        </Button>}
      </Stack>
    </LogFormShell>
  );
};

export default HealthCandidateReviewSheet;
